import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  formatTimeParts,
  isTimePartBeforeMin,
  parseTimeParts,
} from './storeOrderDateUtils';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

function StoreExpectedDeliveryTimePicker({
  id,
  value,
  minTime,
  onChange,
  disabled = false,
  'aria-label': ariaLabel,
}) {
  const rootRef = useRef(null);
  const hourListRef = useRef(null);
  const minuteListRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => parseTimeParts(value), [value]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open || !selected) return;
    const scrollToSelected = (listEl, index) => {
      if (!listEl) return;
      const item = listEl.children[index];
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    };
    scrollToSelected(hourListRef.current, selected.hour);
    scrollToSelected(minuteListRef.current, selected.minute);
  }, [open, selected]);

  const displayValue = value || '--:--';

  const handleHourSelect = (hour) => {
    if (isTimePartBeforeMin(hour, 59, minTime)) return;
    const minute = selected?.minute ?? 0;
    const nextMinute = isTimePartBeforeMin(hour, minute, minTime)
      ? parseTimeParts(minTime)?.minute ?? 0
      : minute;
    onChange(formatTimeParts(hour, nextMinute));
  };

  const handleMinuteSelect = (minute) => {
    const hour = selected?.hour ?? parseTimeParts(minTime)?.hour ?? 0;
    if (isTimePartBeforeMin(hour, minute, minTime)) return;
    onChange(formatTimeParts(hour, minute));
  };

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  return (
    <div
      ref={rootRef}
      className={`store-time-picker${open ? ' store-time-picker--open' : ''}`}
    >
      <button
        id={id}
        type="button"
        className="assign-agents-form__datetime assign-agents-form__datetime--time store-time-picker__trigger"
        onClick={handleToggle}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {displayValue}
      </button>
      {open ? (
        <div className="store-time-picker__panel" role="presentation">
          <div className="store-time-picker__columns">
            <ul
              ref={hourListRef}
              className="store-time-picker__column"
              role="listbox"
              aria-label="Hour"
            >
              {HOURS.map((hour) => {
                const isBeforeMin = isTimePartBeforeMin(hour, 59, minTime);
                const isSelected = selected?.hour === hour;
                return (
                  <li key={hour} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={isBeforeMin}
                      disabled={isBeforeMin}
                      className={[
                        'store-time-picker__option',
                        isSelected ? 'is-selected' : '',
                        isBeforeMin ? 'is-before-min' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleHourSelect(hour)}
                    >
                      {formatTimeParts(hour, 0).slice(0, 2)}
                    </button>
                  </li>
                );
              })}
            </ul>
            <ul
              ref={minuteListRef}
              className="store-time-picker__column"
              role="listbox"
              aria-label="Minute"
            >
              {MINUTES.map((minute) => {
                const hour = selected?.hour ?? parseTimeParts(minTime)?.hour ?? 0;
                const isBeforeMin = isTimePartBeforeMin(hour, minute, minTime);
                const isSelected = selected?.minute === minute;
                return (
                  <li key={minute} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={isBeforeMin}
                      disabled={isBeforeMin}
                      className={[
                        'store-time-picker__option',
                        isSelected ? 'is-selected' : '',
                        isBeforeMin ? 'is-before-min' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleMinuteSelect(minute)}
                    >
                      {formatTimeParts(0, minute).slice(3, 5)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default StoreExpectedDeliveryTimePicker;
